// @ts-nocheck
/**
 * Lets a backend developer point this deployed frontend at their own backend
 * by appending `?API_URL=<backend-origin>` to the app's URL, e.g.
 * `?API_URL=http://localhost:8000`. Works out of the box when the frontend
 * is opened on the SAME machine as the backend (`localhost` is resolved by
 * the browser, so it reaches whatever's running locally there); any other
 * reachable origin (a LAN address, a tunnel URL, etc.) works the same way.
 *
 * The value is persisted to localStorage (mirrors the `loginWithNormalAuth`
 * pattern in `commonAuthConfig.js`) so it survives the auth redirect/session,
 * and the param is stripped from the URL immediately after being read.
 *
 * Off by default. The `/plugins` API's `plugins.apiUrlOverride` boolean flag
 * (read in render.plugin.jsx, fetched in Layout.jsx, and mirrored into
 * localStorage the same way as `dateFormat`/`timeZone`) is the tenant-level
 * kill switch — with it false or missing, the `API_URL` param is ignored
 * entirely, even if a stale override value is still sitting in localStorage
 * from an earlier session.
 */

const API_HOST_OVERRIDE_PARAM = 'API_URL';
const API_HOST_OVERRIDE_STORAGE_KEY = 'API_URL_OVERRIDE';
const API_HOST_OVERRIDE_FEATURE_FLAG_KEY = 'apiUrlOverrideEnabled';

const isFeatureEnabled = () =>
  localStorage.getItem(API_HOST_OVERRIDE_FEATURE_FLAG_KEY) === 'true';

const stripProtocol = (host) => {
  const withoutProtocol = host.replace(/^https?:\/\//, '');
  return withoutProtocol.endsWith('/')
    ? withoutProtocol.slice(0, -1)
    : withoutProtocol;
};

/**
 * Reads the `API_URL` URL param (if present), persists/clears it in
 * localStorage, cleans it off the URL, and returns the currently active
 * override origin (or null if none is set).
 *
 * @return {string|null} The overridden backend origin, e.g. "http://localhost:8000".
 */
export const getApiHostOverride = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const apiHostParam = urlParams.get(API_HOST_OVERRIDE_PARAM);

  if (apiHostParam !== null) {
    if (apiHostParam && isFeatureEnabled()) {
      localStorage.setItem(API_HOST_OVERRIDE_STORAGE_KEY, apiHostParam);
    } else {
      localStorage.removeItem(API_HOST_OVERRIDE_STORAGE_KEY);
    }

    urlParams.delete(API_HOST_OVERRIDE_PARAM);
    const newUrl = `${window.location.pathname}${
      urlParams.toString() ? `?${urlParams.toString()}` : ''
    }${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  if (!isFeatureEnabled()) {
    return null;
  }

  return localStorage.getItem(API_HOST_OVERRIDE_STORAGE_KEY) || null;
};

/**
 * Backend origin API calls should be sent to: the override when set,
 * otherwise the tenant's configured `VAULT_BASE_URL`.
 *
 * @return {string} The backend origin, e.g. "http://localhost:8000".
 */
export const getVaultBaseUrl = () => {
  return getApiHostOverride() || window.CONFIG.VAULT_BASE_URL;
};

/**
 * Hosts that should receive the auth token/org header. Includes the
 * override host (bare, no protocol) alongside the tenant-configured host so
 * the interceptor still attaches auth when requests are redirected.
 *
 * @return {string[]}
 */
export const getHostsNeedToken = () => {
  const override = getApiHostOverride();
  if (!override) {
    return [window.CONFIG.HOSTS_NEEDS_TOKEN];
  }
  return [
    ...new Set([stripProtocol(override), window.CONFIG.HOSTS_NEEDS_TOKEN]),
  ];
};
